import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import Reveal from "@/components/Reveal";

const ABOUT_IMG =
  "https://static.prod-images.emergentagent.com/jobs/6d5b6d04-c2d2-4704-9393-0ecb48523eae/images/83d7bc5c0944242e9976f9158e7169feaba0670c31274cb121bc8d2b2650561a.png";

export default function About() {
  return (
    <div data-testid="about-page" style={{ paddingTop: 168 }}>
      {/* INTRO */}
      <section className="container-wide" style={{ paddingTop: "8rem", paddingBottom: "6rem" }}>
        <Reveal>
          <p className="label-mono">— About / The Institution</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h1
            className="font-display font-bold mt-6"
            style={{
              fontSize: "clamp(2.5rem, 8vw, 7rem)",
              letterSpacing: "-0.045em",
              lineHeight: 0.92,
            }}
          >
            An institution
            <br />
            built for the
            <br />
            <span style={{ color: "var(--mulberry)" }}>next era.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="grid md:grid-cols-12 gap-10 mt-16">
            <div className="md:col-span-5">
              <p className="label-mono">— Premise</p>
              <p className="font-body text-2xl leading-snug mt-6">
                The 20th century optimized for extraction. The 21st must
                optimize for{" "}
                <span style={{ color: "var(--mulberry)", fontStyle: "italic" }}>
                  regeneration
                </span>
                .
              </p>
            </div>
            <div className="md:col-span-6 md:col-start-7">
              <p className="label-mono">— Position</p>
              <p className="font-body text-xl leading-relaxed mt-6">
                Folks Foundation exists to align capital, infrastructure,
                technology, ecology, and human progress into a single
                civilizational framework. We are not a charity. We are a
                long-duration institution underwriting the systems the next
                century will depend on.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* IMAGE BAND */}
      <section data-testid="about-imageband" className="w-full">
        <div
          style={{
            backgroundImage: `url(${ABOUT_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            height: "70vh",
            borderTop: "1px solid var(--soft-border)",
            borderBottom: "1px solid var(--soft-border)",
          }}
          className="relative"
        >
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(17,18,19,0.0) 0%, rgba(17,18,19,0.55) 100%)",
            }}
          />
          <div className="container-wide relative h-full flex items-end pb-12">
            <p
              className="font-display font-bold"
              style={{
                fontSize: "clamp(1.75rem, 4.5vw, 4rem)",
                color: "var(--pearl)",
                letterSpacing: "-0.035em",
                lineHeight: 0.95,
                maxWidth: 900,
              }}
            >
              Where industrial transformation
              <br />
              meets{" "}
              <span style={{ color: "var(--mulberry-bright)" }}>
                ecological salvation.
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* ERA TABLE */}
      <section
        data-testid="about-era-table"
        className="container-wide"
        style={{ paddingTop: "8rem", paddingBottom: "8rem" }}
      >
        <div className="grid md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-5">
            <p className="label-mono mb-4">— The civilizational shift</p>
            <h2
              className="font-display font-bold"
              style={{
                fontSize: "clamp(2rem, 4.5vw, 3.5rem)",
                letterSpacing: "-0.035em",
                lineHeight: 0.95,
              }}
            >
              From the era of{" "}
              <span style={{ textDecoration: "line-through", textDecorationColor: "var(--mulberry)" }}>
                extraction
              </span>{" "}
              to the era of{" "}
              <span style={{ color: "var(--mulberry)" }}>regeneration.</span>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 self-end">
            <p className="font-body text-xl leading-relaxed">
              Five reversals define the next civilizational operating system.
              They are not aspirational — they are structural.
            </p>
          </div>
        </div>

        {/* Freeform diptych compositions — each reversal is a poem block, alternating alignment */}
        <div className="mt-8">
          {[
            ["Oil & fossil energy", "Climate resilience"],
            ["Centralized production", "Decentralized energy"],
            ["Industrial speed", "Ecological recovery"],
            ["Financial acceleration", "Sovereign sustainability"],
            ["Extractive growth", "Social continuity"],
          ].map(([a, b], i) => {
            // Alternate alignment for editorial rhythm: L, R, C, L, R
            const aligns = ["left", "right", "center", "left", "right"];
            const align = aligns[i];
            const alignClass =
              align === "right"
                ? "items-end text-right ml-auto"
                : align === "center"
                  ? "items-center text-center mx-auto"
                  : "items-start text-left";

            return (
              <motion.div
                key={i}
                data-testid={`era-card-${i + 1}`}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 1.1,
                  delay: i * 0.05,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className={`flex flex-col ${alignClass} max-w-3xl`}
                style={{
                  padding: "5rem 0",
                }}
              >
                {/* Marker */}
                <div className="flex items-baseline gap-4 mb-8">
                  <span
                    className="font-display"
                    style={{
                      fontSize: "1.75rem",
                      letterSpacing: "-0.02em",
                      color: "var(--mulberry)",
                      lineHeight: 1,
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="label-mono">— Reversal</span>
                </div>

                {/* Old — small, struck through */}
                <p
                  className="font-display"
                  style={{
                    fontSize: "clamp(1rem, 1.6vw, 1.375rem)",
                    letterSpacing: "0.02em",
                    color: "rgba(17,18,19,0.4)",
                    textTransform: "uppercase",
                    textDecoration: "line-through",
                    textDecorationColor: "rgba(17,18,19,0.3)",
                    textDecorationThickness: "1px",
                  }}
                >
                  We exit {a.toLowerCase()}.
                </p>

                {/* Animated mulberry hairline */}
                <motion.span
                  aria-hidden
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{
                    duration: 1.2,
                    delay: 0.25 + i * 0.05,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  style={{
                    display: "block",
                    width: 72,
                    height: 1,
                    margin: "1.5rem 0",
                    background: "var(--mulberry)",
                    transformOrigin: align === "right" ? "right" : "left",
                    ...(align === "center"
                      ? { transformOrigin: "center" }
                      : {}),
                  }}
                />

                {/* New — massive display statement */}
                <h3
                  className="font-display font-bold"
                  style={{
                    fontSize: "clamp(2.5rem, 7vw, 6rem)",
                    letterSpacing: "-0.045em",
                    lineHeight: 0.92,
                    color: "var(--charcoal)",
                  }}
                >
                  We enter{" "}
                  <span style={{ color: "var(--mulberry)" }}>
                    {b.toLowerCase()}
                  </span>
                  .
                </h3>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* PRINCIPLES */}
      <section
        data-testid="about-principles"
        style={{
          background: "var(--charcoal)",
          color: "var(--pearl)",
          padding: "8rem 0",
        }}
      >
        <div className="container-wide">
          <Reveal>
            <p className="label-mono-light mb-6">— Operating principles</p>
            <h2
              className="font-display font-bold"
              style={{
                fontSize: "clamp(2rem, 5vw, 4rem)",
                letterSpacing: "-0.04em",
                lineHeight: 0.95,
              }}
            >
              The institution
              <br />
              compounds value
              <br />
              <span style={{ color: "var(--mulberry-bright)" }}>
                over generations, not quarters.
              </span>
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-px mt-16" style={{ background: "rgba(247,247,247,0.12)" }}>
            {[
              {
                k: "01",
                t: "Long-duration",
                d: "Time horizons measured in decades — institutions, ecosystems, and human capital deserve longer underwriting than market cycles allow.",
              },
              {
                k: "02",
                t: "Sovereign-grade",
                d: "We work alongside governments, ministries, cities and development finance — not as vendors, but as institutional peers.",
              },
              {
                k: "03",
                t: "Civilizational",
                d: "Progress that does not preserve memory is fragile. We treat culture as infrastructure on equal footing with capital.",
              },
            ].map((p) => (
              <div
                key={p.k}
                style={{ background: "var(--charcoal)" }}
                className="p-10 md:p-12"
              >
                <p className="label-mono-light">— {p.k}</p>
                <h3
                  className="font-display font-bold mt-6"
                  style={{
                    fontSize: "1.75rem",
                    letterSpacing: "-0.03em",
                    color: "var(--mulberry-bright)",
                  }}
                >
                  {p.t}
                </h3>
                <p className="font-body text-lg leading-relaxed mt-4 opacity-85">
                  {p.d}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-16">
            <Link to="/vision" data-testid="about-cta-vision" className="btn-ghost-light">
              Read the vision <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
